import React, { useState, useEffect, useCallback } from 'react';
import styled from '@emotion/styled';
import { motion } from 'framer-motion';
import { CalendarDays, Clock, Check } from 'lucide-react';
import { CalendarSlot, Task } from '../types/task';
// import { fetchCalendarSlots, rescheduleTask } from '../services/tasks';
import { format, addDays } from 'date-fns';

const CalendarContainer = styled.div`
  background: white;
  border-radius: 20px;
  padding: 32px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.08);
  margin-bottom: 32px;
  border: 1px solid #f1f5f9;
`;

const CalendarHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 32px;
`;

const CalendarTitle = styled.h3`
  margin: 0;
  font-size: 28px;
  font-weight: 800;
  background: linear-gradient(135deg, #F57C00 0%, #E65100 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  letter-spacing: -0.5px;
`;

const DateNavigation = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  background: #f8fafc;
  padding: 8px;
  border-radius: 12px;
  border: 1px solid #e2e8f0;
`;

const NavButton = styled(motion.button)`
  background: white;
  border: none;
  border-radius: 8px;
  padding: 12px 16px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 600;
  color: #374151;
  transition: all 0.2s;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);

  &:hover {
    background: #F57C00;
    color: white;
    transform: translateY(-1px);
    box-shadow: 0 4px 8px rgba(245, 124, 0, 0.3);
  }
`;

const CurrentDate = styled.span`
  font-size: 18px;
  font-weight: 700;
  color: #1e293b;
  min-width: 140px;
  text-align: center;
  padding: 12px 16px;
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
`;

const TimeSlotsContainer = styled.div`
  display: grid;
  gap: 16px;
`;

const TimeSlot = styled(motion.div)<{ isAvailable: boolean; hasTask: boolean }>`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 24px;
  border-radius: 12px;
  border: 2px solid ${props => props.hasTask ? '#4CAF50' : props.isAvailable ? '#e2e8f0' : '#f1f5f9'};
  background-color: ${props => {
    if (props.hasTask) return '#E8F5E8';
    return props.isAvailable ? 'white' : '#f8fafc';
  }};
  cursor: ${props => props.isAvailable ? 'pointer' : 'not-allowed'};
  transition: all 0.2s;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);

  &:hover {
    ${props => props.isAvailable && `
      border-color: #F57C00;
      box-shadow: 0 4px 16px rgba(245, 124, 0, 0.15);
      transform: translateY(-2px);
    `}
  }
`;

const TimeInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const TimeRange = styled.span`
  font-weight: 600;
  color: #1f2937;
`;

const Duration = styled.span`
  font-size: 14px;
  color: #6b7280;
`;

const SlotStatus = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 14px;
`;

const StatusBadge = styled.span<{ status: 'available' | 'busy' | 'scheduled' }>`
  padding: 4px 8px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 500;
  background-color: ${props => {
    switch (props.status) {
      case 'available': return '#E3F2FD';
      case 'busy': return '#FFEBEE';
      case 'scheduled': return '#E8F5E8';
    }
  }};
  color: ${props => {
    switch (props.status) {
      case 'available': return '#1976D2';
      case 'busy': return '#D32F2F';
      case 'scheduled': return '#388E3C';
    }
  }};
`;

const TaskInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  color: #059669;
  font-size: 14px;
  font-weight: 500;
`;

interface CalendarProps {
  userId: string;
  selectedTask?: Task;
  onTaskScheduled: () => void;
}

const Calendar: React.FC<CalendarProps> = ({ userId, selectedTask, onTaskScheduled }) => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [timeSlots, setTimeSlots] = useState<CalendarSlot[]>([]);
  const [loading, setLoading] = useState(false);

  const generateTimeSlots = () => {
    const slots: CalendarSlot[] = [];
    const startHour = 8; // 8 AM
    const endHour = 20; // 8 PM
    const slotDuration = 30; // 30 minutes

    for (let hour = startHour; hour < endHour; hour++) {
      for (let minute = 0; minute < 60; minute += slotDuration) {
        const startTime = new Date(currentDate);
        startTime.setHours(hour, minute, 0, 0);
        
        const endTime = new Date(startTime);
        endTime.setMinutes(endTime.getMinutes() + slotDuration);

        slots.push({
          startTime,
          endTime,
          isAvailable: true,
        });
      }
    }

    return slots;
  };

  const loadTimeSlots = useCallback(async () => {
    try {
      setLoading(true);
      
      // For demo purposes, use the generated time slots
      const slots = generateTimeSlots();
      setTimeSlots(slots);
      
      // Uncomment this for real API calls
      // const slots = await fetchCalendarSlots(userId, currentDate);
      // setTimeSlots(slots);
    } catch (error) {
      console.error('Error loading time slots:', error);
    } finally {
      setLoading(false);
    }
  }, [generateTimeSlots]);

  useEffect(() => {
    loadTimeSlots();
  }, [loadTimeSlots]);

  const handleDateChange = (direction: 'prev' | 'next') => {
    setCurrentDate(prev => {
      if (direction === 'prev') {
        return addDays(prev, -1);
      } else {
        return addDays(prev, 1);
      }
    });
  };

  const handleSlotClick = async (slot: CalendarSlot) => {
    if (!slot.isAvailable || !selectedTask) return;

    try {
      // For demo purposes, simulate task rescheduling
      console.log('Rescheduling task:', selectedTask.id, 'to slot:', slot.startTime);
      await new Promise(resolve => setTimeout(resolve, 1000)); // Simulate API call
      
      onTaskScheduled();
      await loadTimeSlots(); // Refresh slots
    } catch (error) {
      console.error('Error scheduling task:', error);
      alert('Failed to schedule task. Please try again.');
    }
  };

  const formatTime = (date: Date) => {
    return format(date, 'h:mm a');
  };

  const getSlotStatus = (slot: CalendarSlot) => {
    if (slot.taskId) return 'scheduled';
    if (!slot.isAvailable) return 'busy';
    return 'available';
  };

  const getSlotStatusText = (slot: CalendarSlot) => {
    if (slot.taskId) return 'Scheduled';
    if (!slot.isAvailable) return 'Busy';
    return 'Available';
  };

  if (loading) {
    return (
      <CalendarContainer>
        <div>Loading calendar...</div>
      </CalendarContainer>
    );
  }

  // For demo purposes, generate time slots if none are returned from API
  const displaySlots = timeSlots.length > 0 ? timeSlots : generateTimeSlots();

  return (
    <CalendarContainer>
      <CalendarHeader>
        <CalendarTitle>
          <CalendarDays style={{ marginRight: '8px' }} />
          Schedule Your Mission
        </CalendarTitle>
        <DateNavigation>
          <NavButton
            onClick={() => handleDateChange('prev')}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            Previous
          </NavButton>
          <CurrentDate>
            {format(currentDate, 'MMM dd, yyyy')}
          </CurrentDate>
          <NavButton
            onClick={() => handleDateChange('next')}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            Next
          </NavButton>
        </DateNavigation>
      </CalendarHeader>

      {selectedTask && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          style={{
            background: '#eff6ff',
            padding: '12px 16px',
            borderRadius: '8px',
            marginBottom: '16px',
            border: '1px solid #3b82f6',
          }}
        >
          <strong>Selected Task:</strong> {selectedTask.title}
        </motion.div>
      )}

      <TimeSlotsContainer>
        {displaySlots.map((slot, index) => (
          <TimeSlot
            key={index}
            isAvailable={slot.isAvailable}
            hasTask={!!slot.taskId}
            onClick={() => handleSlotClick(slot)}
            whileHover={slot.isAvailable ? { scale: 1.02 } : {}}
            whileTap={slot.isAvailable ? { scale: 0.98 } : {}}
          >
            <TimeInfo>
              <Clock />
              <TimeRange>
                {formatTime(slot.startTime)} - {formatTime(slot.endTime)}
              </TimeRange>
              <Duration>(30 min)</Duration>
            </TimeInfo>

            <SlotStatus>
              {slot.taskId ? (
                <TaskInfo>
                  <Check />
                  Task Scheduled
                </TaskInfo>
              ) : (
                <StatusBadge status={getSlotStatus(slot)}>
                  {getSlotStatusText(slot)}
                </StatusBadge>
              )}
            </SlotStatus>
          </TimeSlot>
        ))}
      </TimeSlotsContainer>

      {!selectedTask && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          style={{
            textAlign: 'center',
            padding: '20px',
            color: '#6b7280',
            fontStyle: 'italic',
          }}
        >
          Select a task from your mission list to schedule it
        </motion.div>
      )}
    </CalendarContainer>
  );
};

export default Calendar; 